import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import axios from "axios";

import { clearPreauthSession } from "../components/helpers/authFlowStorage";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("active_app_id");
    localStorage.removeItem("subscription_status");
    clearPreauthSession();
  }, []);
  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    const activeAppId = localStorage.getItem("active_app_id");

    // console.log("for-test refreshUser start", {hasToken: Boolean(token),activeAppId,});

    if (!token) {
      setUser(null);
      setLoadingUser(false);
      return null;
    }

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: activeAppId ? { active_app_id: activeAppId } : {},
        }
      );

      // console.log("for-test refreshUser success", res.data);

      setUser(res.data);
      return res.data;
    } catch (err) {
      const status = err?.response?.status;

      // console.log("for-test refreshUser failed", {status,detail: err?.response?.data,});

      if (status !== 401) {
        console.error("Auth check failed:", err);
      }

      clearStoredSession();
      setUser(null);
      return null;
    } finally {
      setLoadingUser(false);
    }
  }, [clearStoredSession]);

  const applySession = useCallback(
    async ({
      token,
      role,
      activeAppId,
      subscriptionStatus,
      clearPreauth = true,
    }) => {
      if (!token) {
        throw new Error("applySession requires token.");
      }

      localStorage.setItem("token", String(token));
      localStorage.setItem("role", String(role || ""));
      localStorage.setItem("active_app_id", String(activeAppId || ""));
      localStorage.setItem(
        "subscription_status",
        String(subscriptionStatus || "")
      );

      if (clearPreauth) {
        clearPreauthSession();
      }

      return await refreshUser();
    },
    [refreshUser]
  );

  const logout = useCallback(() => {
    clearStoredSession();
    setUser(null);
    setLoadingUser(false);
  }, [clearStoredSession]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loadingUser,
        refreshUser,
        applySession,
        logout,
        clearStoredSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;